import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { formatCurrency, formatDate } from '@/lib/utils';
import { CreditCard, Users, CheckCircle, Clock, History, Receipt } from 'lucide-react';
import type { Invoice } from '@/types';

const formatTime = (date: Date): string =>
  date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });

export interface PaymentSummaryBreakdownRow {
  key: string;
  label: string;
  ticketDescription: string;
  ticketPrice: number;
  bookingFee: number;
  lineTotal: number;
  paidBadge?: 'paid' | 'pending' | null;
}

interface PaymentSummaryCardProps {
  invoice: Invoice | null;
  totalGuests: number;
  displayTotal: number;
  amountPaid: number;
  outstandingBalance: number;
  breakdownRows: PaymentSummaryBreakdownRow[];
  onPayNow: () => void;
  showPaymentHistoryButton?: boolean;
}

/** Presentational payment summary card shared by the meal-choice and multi-tenant RSVP dashboards. */
export function PaymentSummaryCard({
  invoice,
  totalGuests,
  displayTotal,
  amountPaid,
  outstandingBalance,
  breakdownRows,
  onPayNow,
  showPaymentHistoryButton = true,
}: PaymentSummaryCardProps) {
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);

  return (
    <Card className="shadow-lg">
      <CardHeader className="pb-6">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-3 text-2xl">
            <div className="p-3 bg-blue-100 rounded-xl">
              <CreditCard className="h-6 w-6 text-blue-600" />
            </div>
            Payment Summary
          </CardTitle>
          <Dialog open={showPaymentHistoryModal} onOpenChange={setShowPaymentHistoryModal}>
            {showPaymentHistoryButton && (
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="flex items-center gap-2">
                  <History className="h-4 w-4" />
                  Payment History
                </Button>
              </DialogTrigger>
            )}
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Receipt className="h-5 w-5" />
                  Payment History
                </DialogTitle>
                <DialogDescription>Complete history of all payments made for this event</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {invoice?.paymentHistory && invoice.paymentHistory.length > 0 ? (
                  <div className="space-y-3">
                    {invoice.paymentHistory.map((payment, index) => (
                      <Card key={index}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-green-600" />
                                <span className="font-medium">Payment #{index + 1}</span>
                                <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
                                  Completed
                                </Badge>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <span className="text-muted-foreground">Amount:</span>
                                  <p className="font-medium">{formatCurrency(payment.amount)}</p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Date:</span>
                                  <p className="font-medium">
                                    {formatDate(payment.date)} at {formatTime(payment.date)}
                                  </p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Method:</span>
                                  <p className="font-medium">{payment.method || 'Card Payment'}</p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Card processing fee:</span>
                                  <p className="font-medium">{formatCurrency(payment.processingFee)}</p>
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-green-600">{formatCurrency(payment.amount)}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    <div className="pt-4 border-t">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Total Paid:</span>
                        <span className="text-xl font-bold text-green-600">{formatCurrency(amountPaid)}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Receipt className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">No payment history available</p>
                    <p className="text-sm text-muted-foreground">Payments will appear here once they are processed</p>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Payment Metrics Grid */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            <div className="min-w-0 rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="shrink-0 rounded-lg bg-blue-200 p-1.5 sm:p-2">
                  <Users className="h-4 w-4 text-blue-700 sm:h-5 sm:w-5" />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-xs font-medium text-blue-800 sm:text-sm">Total Guests</p>
                  <p className="truncate text-lg font-bold text-blue-900 sm:text-2xl">{totalGuests}</p>
                </div>
              </div>
            </div>

            <div className="min-w-0 rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="shrink-0 rounded-lg bg-slate-200 p-1.5 sm:p-2">
                  <CreditCard className="h-4 w-4 text-slate-700 sm:h-5 sm:w-5" />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-xs font-medium text-slate-800 sm:text-sm">Total Amount</p>
                  <p className="truncate text-lg font-bold text-slate-900 sm:text-2xl">{formatCurrency(displayTotal)}</p>
                </div>
              </div>
            </div>

            <div className="min-w-0 rounded-xl border border-green-200 bg-gradient-to-br from-green-50 to-green-100 p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="shrink-0 rounded-lg bg-green-200 p-1.5 sm:p-2">
                  <CheckCircle className="h-4 w-4 text-green-700 sm:h-5 sm:w-5" />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-xs font-medium text-green-800 sm:text-sm">Amount Paid</p>
                  <p className="truncate text-lg font-bold text-green-900 sm:text-2xl">{formatCurrency(amountPaid)}</p>
                </div>
              </div>
            </div>

            <div
              className={`min-w-0 rounded-xl border p-3 sm:p-4 ${
                outstandingBalance > 0
                  ? 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200'
                  : outstandingBalance < 0
                    ? 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200'
                    : 'bg-gradient-to-br from-green-50 to-green-100 border-green-200'
              }`}
            >
              <div className="flex items-center gap-2 sm:gap-3">
                <div
                  className={`shrink-0 rounded-lg p-1.5 sm:p-2 ${
                    outstandingBalance > 0 ? 'bg-orange-200' : outstandingBalance < 0 ? 'bg-blue-200' : 'bg-green-200'
                  }`}
                >
                  {outstandingBalance > 0 ? (
                    <Clock className="h-4 w-4 text-orange-700 sm:h-5 sm:w-5" />
                  ) : outstandingBalance < 0 ? (
                    <CreditCard className="h-4 w-4 text-blue-700 sm:h-5 sm:w-5" />
                  ) : (
                    <CheckCircle className="h-4 w-4 text-green-700 sm:h-5 sm:w-5" />
                  )}
                </div>
                <div className="min-w-0">
                  <p
                    className={`truncate text-xs font-medium sm:text-sm ${
                      outstandingBalance > 0 ? 'text-orange-800' : outstandingBalance < 0 ? 'text-blue-800' : 'text-green-800'
                    }`}
                  >
                    {outstandingBalance > 0 ? 'Outstanding' : outstandingBalance < 0 ? 'Credit' : 'Outstanding'}
                  </p>
                  <p
                    className={`truncate text-lg font-bold sm:text-2xl ${
                      outstandingBalance > 0 ? 'text-orange-900' : outstandingBalance < 0 ? 'text-blue-900' : 'text-green-900'
                    }`}
                  >
                    {formatCurrency(Math.abs(outstandingBalance))}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Breakdown */}
          <div className="bg-gradient-to-br from-muted/30 to-muted/60 rounded-xl p-6 border">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Receipt className="h-5 w-5 text-primary" />
              </div>
              <h4 className="text-lg font-semibold text-foreground">Payment Breakdown</h4>
            </div>

            <div className="space-y-2">
              {breakdownRows.map((row) => (
                <div key={row.key} className="flex items-center justify-between rounded-lg bg-background/60 p-3">
                  <div>
                    <p className="font-medium text-foreground">{row.label}</p>
                    <p className="text-sm text-muted-foreground">{row.ticketDescription}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {row.paidBadge === 'paid' && (
                      <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
                        Paid
                      </Badge>
                    )}
                    {row.paidBadge === 'pending' && (
                      <Badge variant="secondary">Pending</Badge>
                    )}
                    <p className="font-semibold text-foreground">{formatCurrency(row.lineTotal)}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-border/50 pt-4 mt-4">
              <div className="flex items-center justify-between p-3 bg-primary/5 rounded-lg">
                <span className="text-lg font-bold text-foreground">Your Total</span>
                <span className="text-2xl font-bold text-primary">{formatCurrency(displayTotal)}</span>
              </div>
            </div>
          </div>

          {invoice?.status === 'pending_verification' && (
            <div className="pt-4 border-t">
              <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950">
                <Clock className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">Payment awaiting approval</p>
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    Your bank transfer has been received and is pending review by the treasurer. You will be notified once it has been approved.
                  </p>
                </div>
              </div>
            </div>
          )}
          {invoice?.status === 'paid' && amountPaid > 0 && (
            <div className="pt-4 border-t">
              <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950">
                <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-green-600 dark:text-green-400" />
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-green-800 dark:text-green-200">Payment approved</p>
                  <p className="text-xs text-green-700 dark:text-green-300">
                    Your payment of {formatCurrency(amountPaid)} has been approved and your booking is confirmed. Thank you.
                  </p>
                </div>
              </div>
            </div>
          )}

          {outstandingBalance > 0 && invoice?.status !== 'pending_verification' && (
            <div className="pt-4 border-t">
              <Button type="button" className="w-full h-11 touch-manipulation" onClick={onPayNow}>
                <CreditCard className="mr-2 h-4 w-4" />
                Pay Now
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
